import axios from '../AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';

const base = (companyId) => `${companyId}/documents`;
const tagBase = (companyId) => `${companyId}/document-tags`;

// The blob container is private — content must be streamed through the
// backend using its storage credentials. The proxy endpoint needs a Bearer
// token in the Authorization header, which browsers don't send on
// <img src> / <iframe src>. So we fetch via axios (which IS authed) and
// hand <img>/<iframe> a blob: URL via URL.createObjectURL. Callers are
// responsible for revoking the URL when unmounting to avoid leaks.
export const fetchContentBlob = async (companyId, blobName) => {
  const path = `${base(companyId)}/${encodeURIComponent(blobName)}/content`;
  const res = await axios.get(getApiUrl(path), { responseType: 'blob', withCredentials: true });
  return URL.createObjectURL(res.data);
};

// Raw ArrayBuffer — used by pdfjs for thumbnail rendering where we need
// the binary content, not a blob: URL.
export const fetchContentArrayBuffer = async (companyId, blobName) => {
  const path = `${base(companyId)}/${encodeURIComponent(blobName)}/content`;
  const res = await axios.get(getApiUrl(path), { responseType: 'arraybuffer', withCredentials: true });
  return res.data;
};

// Force-download helper: fetches → blob: URL → synthesized <a download> click.
// Used by the "Ladda ner" button.
export const downloadContentBlob = async (companyId, blobName, suggestedFilename) => {
  const path = `${base(companyId)}/${encodeURIComponent(blobName)}/content?disposition=attachment`;
  const res = await axios.get(getApiUrl(path), { responseType: 'blob', withCredentials: true });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = suggestedFilename || blobName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the browser has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const listDocuments = (companyId, params) =>
  axios.get(getApiUrl(base(companyId)), { params, withCredentials: true });

// Standalone upload. `fields` is { description?, validFrom?, validTo?, tagIds? (array of uuid) }
export const uploadStandalone = (companyId, file, fields = {}) => {
  const form = new FormData();
  form.append('file', file);
  if (fields.description) form.append('description', fields.description);
  if (fields.validFrom) form.append('validFrom', fields.validFrom);
  if (fields.validTo) form.append('validTo', fields.validTo);
  if (Array.isArray(fields.tagIds) && fields.tagIds.length > 0) {
    form.append('tagIds', JSON.stringify(fields.tagIds));
  }
  return axios.post(getApiUrl(`${base(companyId)}/upload`), form, {
    withCredentials: true,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// Update description, validity, or tags on an existing blob.
export const patchDocument = (companyId, blobName, data) =>
  axios.patch(getApiUrl(`${base(companyId)}/${encodeURIComponent(blobName)}`), data, { withCredentials: true });

// "Behåll i arkivet" — promote a stale temp upload to kept archive.
export const archiveDocument = (companyId, blobName) =>
  axios.post(getApiUrl(`${base(companyId)}/${encodeURIComponent(blobName)}/archive`), {}, { withCredentials: true });

// Admin hard delete.
export const deleteDocument = (companyId, blobName) =>
  axios.delete(getApiUrl(`${base(companyId)}/${encodeURIComponent(blobName)}`), { withCredentials: true });

// Storage quota + live usage for the company's plan. Returns
//   { usage: { storageBytes, … }, limits: { maxStorageGb, … }, plan, … }
// Reconciled against actual blob listing on every call.
export const getResourceUsage = (companyId) =>
  axios.get(getApiUrl(`${companyId}/subscription/resource-usage`), { withCredentials: true });

// Tag vocabulary.
export const listTags = (companyId) =>
  axios.get(getApiUrl(tagBase(companyId)), { withCredentials: true });

export const createTag = (companyId, data) =>
  axios.post(getApiUrl(tagBase(companyId)), data, { withCredentials: true });

export const deleteTag = (companyId, tagId) =>
  axios.delete(getApiUrl(`${tagBase(companyId)}/${tagId}`), { withCredentials: true });
